import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Marquee } from '@sorare/core/src/atoms/container/Marquee';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyL, DisplayL } from '@sorare/core/src/atoms/typography';
import { isStaging } from '@sorare/core/src/config';
import { useConnectionContext } from '@sorare/core/src/contexts/connection';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { LeagueImage } from '@sorare/gameplay/src/components/league/LeagueImage';

import { Chart } from 'components/landing/EarlyAccess/Chart';

import backgroundAvif from './assets/background.avif';
import background from './assets/background.jpg';
import playersAvif from './assets/players.avif';
import players from './assets/players.png';

const leagues = [
  {
    id: 'u23',
    pictureUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-under_twenty_one/picture.jpg',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-under_twenty_one/header.svg',
  },
  {
    id: 'seasonal-spain',
    pictureUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-spain/picture-in_season.jpg',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-spain/header.svg',
  },
  {
    id: 'seasonal-england',
    pictureUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-england/picture-in_season.jpg',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-england/header.svg',
  },
  {
    id: 'seasonal-germany',
    pictureUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-germany/picture-in_season.jpg',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-germany/header.svg',
  },
  {
    id: 'seasonal-champions',
    pictureUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-champions/picture-in_season.jpg',
    headerUrl:
      'https://frontend-assets.sorare.com/football/so5_league/seasonal-champions/header.svg',
  },
];

const ImageWrapper = styled(ButtonBase)`
  position: relative;
  isolation: isolate;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  background-image: image-set(
    url(${backgroundAvif}) type('image/avif'),
    url(${background}) type('image/jpeg')
  );
  background-size: cover;
  background-position: center bottom;
  border-radius: 20px 20px 0 0;
  min-height: 560px;
  margin-bottom: calc(-1 * var(--quadruple-unit));
  &::before {
    position: absolute;
    inset: 0;
    content: '';
    background-image: linear-gradient(
      to left,
      rgba(var(--c-rgb-neutral-100), 0.5),
      transparent
    );
    @media ${laptopAndAbove} {
      background-image: linear-gradient(
          to top,
          var(--c-neutral-100),
          var(--c-neutral-100) 5%,
          transparent 40%
        ),
        linear-gradient(
          to right,
          rgba(var(--c-rgb-neutral-100), 0.5),
          transparent 20%
        );
    }
  }
`;
const Content = styled(Vertical)`
  position: relative;
  text-align: left;
  max-width: 380px;
  width: 100%;
  margin: auto;
  padding: var(--double-unit);
  @media ${laptopAndAbove} {
    position: absolute;
    margin-left: 10%;
    align-items: flex-start;
  }
`;
const Players = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  flex: 1;
  padding-bottom: calc(8 * var(--unit));
  background-image: image-set(
    url(${playersAvif}) type('image/avif'),
    url(${players}) type('image/jpeg')
  );
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center top -5%;
  min-width: 100%;
  max-width: 100%;
  @media ${laptopAndAbove} {
    min-width: none;
    height: 100%;
    background-size: 900px;
    background-position: right -20% top;
  }
`;

const LeagueImageWrapper = styled.button`
  border-radius: 20px;
  overflow: hidden;
  width: 300px;
`;
const ChartWrapper = styled.div`
  transform: scale(0.75);
  transform-origin: bottom;
  margin-top: calc(10 * var(--unit));
  @media ${laptopAndAbove} {
    margin: 0 15% 0 auto;
  }
`;

export const Scout = () => {
  const { signUp } = useConnectionContext();
  return (
    <Vertical gap={0}>
      <Container>
        <ImageWrapper onClick={signUp}>
          <Content gap={5}>
            <Vertical gap={3}>
              <DisplayL>
                <FormattedMessage
                  id="landing.EarlyAccess.Scout.title"
                  defaultMessage="Scout"
                />
              </DisplayL>
              <BodyL>
                <FormattedMessage
                  id="landing.EarlyAccess.Scout.description"
                  defaultMessage="Build your club from the ground up. Use your knowledge to buy, trade and sell players on our marketplace."
                />
              </BodyL>
            </Vertical>
            <Button asSpan color="secondary" size="large">
              <FormattedMessage
                id="landing.EarlyAccess.Scout.cta"
                defaultMessage="Create your club"
              />
            </Button>
          </Content>
          <Players>
            <ChartWrapper>
              <Chart slug={isStaging ? 'joshua-kimmich' : 'erling-haland'} />
            </ChartWrapper>
          </Players>
        </ImageWrapper>
      </Container>
      <Marquee direction="reverse">
        {leagues.map(({ id, ...league }) => (
          <LeagueImageWrapper key={id} type="button" onClick={signUp}>
            <LeagueImage {...league} />
          </LeagueImageWrapper>
        ))}
      </Marquee>
    </Vertical>
  );
};
